import { useDebouncedValue } from '@mantine/hooks';
import { MRT_ColumnFiltersState } from 'mantine-react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getLotsStatsFilters } from '../../lib/lots-filters';
import { LotListItem } from '../../lib/types/lots';

import { useColFilter, useFiltersStore } from '@data-access';

export type LotsQuickFilter = 'pending' | 'tonnage_delta' | 'with_calculated_area';

export { getLotsStatsFilters };

const QUICK_FILTER_IDS: Record<LotsQuickFilter, string> = {
  pending: 'is_validated',
  tonnage_delta: 'has_tonnage_discrepancy',
  with_calculated_area: 'calculated_area__isnull',
};

export function useLotsFilterOptions(results: LotListItem[]) {
  return useMemo(() => {
    const yearSet = new Set<number>();
    const speciesMap = new Map<number, string>();

    results.forEach((lot) => {
      yearSet.add(lot.year);
      if (lot.species) {
        speciesMap.set(lot.species.id, lot.species.name);
      }
    });

    const defaultYears = [2026, 2025, 2024, 2023, 2022];
    defaultYears.forEach((y) => yearSet.add(y));

    const years = Array.from(yearSet).sort((a, b) => b - a);
    const species = Array.from(speciesMap.entries())
      .map(([id, name]) => ({ value: id.toString(), label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return { years, species };
  }, [results]);
}

export function useLotsFilters() {
  const filters = useFiltersStore.use['lots']().filters;
  const setFilter = useFiltersStore((s) => s.setFilter);
  const resetFilter = useFiltersStore((s) => s.resetFilter);
  const setPagination = useFiltersStore((s) => s.setPagination);

  const farmNameFilter = filters.find((f) => f.id === 'farm_name')?.value as
    | string
    | undefined;
  const [searchInput, setSearchInput] = useState(farmNameFilter ?? '');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  const skipInitialSearchSync = useRef(true);

  const [year, setYear] = useColFilter<string | null>('lots', 'year');
  const [speciesId, setSpeciesId] = useColFilter<string | null>('lots', 'species__id');
  const [validated, setValidated] = useColFilter<boolean | null>('lots', 'is_validated');

  const resetPage = useCallback(() => {
    setPagination('lots', { pageIndex: 0, pageSize: 25 });
  }, [setPagination]);

  useEffect(() => {
    if (skipInitialSearchSync.current) {
      skipInitialSearchSync.current = false;
      if (!debouncedSearch.trim() && !farmNameFilter) {
        return;
      }
    }

    const next = debouncedSearch.trim();
    if (next) {
      setFilter('lots', 'farm_name', next);
    } else {
      resetFilter('lots', 'farm_name');
    }
    resetPage();
  }, [
    debouncedSearch,
    farmNameFilter,
    resetFilter,
    resetPage,
    setFilter,
  ]);

  const quickFilters = useMemo(() => {
    const active = new Set<LotsQuickFilter>();
    if (filters.some((f) => f.id === 'is_validated' && f.value === false)) {
      active.add('pending');
    }
    if (
      filters.some(
        (f) => f.id === QUICK_FILTER_IDS.tonnage_delta && f.value === true,
      )
    ) {
      active.add('tonnage_delta');
    }
    if (
      filters.some(
        (f) =>
          f.id === QUICK_FILTER_IDS.with_calculated_area && f.value === false,
      )
    ) {
      active.add('with_calculated_area');
    }
    return active;
  }, [filters]);

  const toggleQuickFilter = useCallback(
    (key: LotsQuickFilter) => {
      const filterId = QUICK_FILTER_IDS[key];
      const isActive = quickFilters.has(key);

      if (key === 'pending') {
        if (isActive) {
          resetFilter('lots', 'is_validated');
        } else {
          setFilter('lots', 'is_validated', false);
          setValidated(false);
        }
      } else if (key === 'with_calculated_area') {
        if (isActive) {
          resetFilter('lots', filterId);
        } else {
          setFilter('lots', filterId, false);
        }
      } else if (isActive) {
        resetFilter('lots', filterId);
      } else {
        setFilter('lots', filterId, true);
      }
      resetPage();
    },
    [quickFilters, resetFilter, resetPage, setFilter, setValidated],
  );

  const handleYearChange = useCallback(
    (value: string | null) => {
      if (value) {
        setYear(value);
      } else {
        resetFilter('lots', 'year');
      }
      resetPage();
    },
    [resetFilter, resetPage, setYear],
  );

  const handleSpeciesChange = useCallback(
    (value: string | null) => {
      if (value) {
        setSpeciesId(value);
      } else {
        resetFilter('lots', 'species__id');
      }
      resetPage();
    },
    [resetFilter, resetPage, setSpeciesId],
  );

  const handleStatusChange = useCallback(
    (value: string | null) => {
      if (value === null) {
        resetFilter('lots', 'is_validated');
        setValidated(null);
      } else {
        const boolValue = value === 'true';
        setValidated(boolValue);
        setFilter('lots', 'is_validated', boolValue);
      }
      resetPage();
    },
    [resetFilter, resetPage, setFilter, setValidated],
  );

  const statusValue =
    validated === true ? 'true' : validated === false ? 'false' : null;

  return {
    filters,
    searchInput: searchInput ?? '',
    setSearchInput,
    year: year?.toString() ?? null,
    speciesId,
    statusValue,
    quickFilters,
    toggleQuickFilter,
    handleYearChange,
    handleSpeciesChange,
    handleStatusChange,
  };
}
